import React from 'react';
import { Field, Form } from 'formik';
import { Label } from 'flowbite-react';

import {
    Months, Days, Years, timezoneOptions,
} from '@/hooks/calendar_helpers';
import SingleSelect from '@/components/forms/select/SingleSelect';
import FormError from '@/components/forms/FormError';
import FileUpload from '@/components/forms/file-upload/';
import LinkedIn from '@/components/commons/icons/LinkedIn';
import routes from '@/services/routes';

import Input from './Input';
import styles from './Personalize.module.css';
import bioStyles from './Bio.module.css';

function PersonalizeForm({
    status,
    selectedMonth,
    handleDaySelect,
    handleYearSelect,
    handleMonthSelect,
    handleChange,
    setStatus,
    buttonRef,
}) {
    return (
        <Form className="h-full w-full flex flex-col">
            <div className="h-full w-full flex flex-col lg:flex-row justify-between">
                <div className="w-full lg:pr-12">
                    <Label
                        htmlFor="fullName"
                        className="block mb-2 text-sm font-medium text-cleanSlate dark:text-cirrusWhite"
                        value="What should we call you?"
                    />
                    <Field
                        id="fullName"
                        name="fullName"
                        palceholder="full name"
                        component={Input}
                        onChange={(e) => {
                            if (status?.errors?.fullName) {
                                setStatus({ errors: {} });
                            }
                            handleChange(e);
                        }}
                    />
                    <p id="helper-text-explanation" className="mt-2 text-sm text-cleanSlate dark:text-gray-400 ">
                        We&apos;ll never share your details. Read our
                        {' '}
                        <a href={routes.PRIVACY_POLICY} target="_blank" className={bioStyles.link} rel="noreferrer">Privacy Policy</a>
                    </p>
                    <div className="mt-5">
                        <div className="flex font-normal text-cleanSlate text-sm">

                            <Label
                                htmlFor="company"
                                className="block mb-2 text-sm font-medium text-cleanSlate dark:text-cirrusWhite"
                                value="Where do you currently work?"
                            />
                            <span className="bg-lightGray text-xs mb-2 ml-2 px-2 text-cleanSlate font-medium leading-[21px] rounded h-[20px]">Optional</span>

                        </div>
                        <Field
                            id="company"
                            name="company"
                            placeholder="Enter your organization name"
                            component={Input}
                        />
                    </div>
                    <div className="mt-5">
                        <div className="flex font-normal text-cleanSlate text-sm">
                            <Label
                                htmlFor="title"
                                className="block mb-2 text-sm font-medium text-cleanSlate dark:text-cirrusWhite"
                                value="What is your current title or role?"
                            />
                            <span className="bg-lightGray text-xs mb-2 ml-2 px-2 font-medium rounded leading-[21px] h-[20px]">Optional</span>

                        </div>

                        <Field
                            id="title"
                            name="title"
                            component={Input}
                            placeholder="Enter your job title"
                        />
                    </div>
                    <div className="pb-4 pt-6">
                        <div className="flex font-normal text-cleanSlate text-sm">
                            <Label
                                htmlFor="linkedIn"
                                className="block mb-1 text-sm font-medium text-cleanSlate dark:text-cirrusWhite"
                                value="What is your LinkedIn profile URL?"
                            />
                            <span className="bg-lightGray text-xs mb-2 ml-2 px-2 font-medium leading-[21px] rounded h-[20px]">Recommended</span>
                        </div>

                        <Field
                            id="linkedIn"
                            name="linkedIn"
                            component={Input}
                            icon={LinkedIn}
                            placeholder="https://linkedin.com/in/ryan.cornell-hayes"
                        />
                        <p id="helper-text-explanation" className="text-sm text-cleanSlate dark:text-gray-400 mt-2">
                            This will help me write the first draft of your bio.
                            Don&apos;t know your URL?
                            {' '}

                            <button
                                type="button"
                                onClick={() => {
                                    if (buttonRef.current) {
                                        buttonRef.current.submit();
                                    }
                                }}
                                className="text-progressBarBlue"
                            >
                                Sign in to LinkedIn
                            </button>
                            {' '}
                            to find it
                        </p>
                    </div>
                    <div className="mb-5">
                        <div className="flex font-normal text-cleanSlate text-sm">
                            <Label
                                htmlFor="timezone"
                                className="block mb-2 text-sm font-medium text-cleanSlate dark:text-cirrusWhite"
                                value="What time zone are you in?"
                            />
                            <span className="bg-lightGray text-xs mb-2 ml-2 px-2 font-medium rounded leading-[21px] h-[20px]">Optional</span>

                        </div>

                        <SingleSelect
                            options={timezoneOptions}
                            name="timezone"
                            placeholder="Select your time zone"
                        />
                    </div>
                </div>
                <div className="w-full">
                    <div className="text-cleanSlate rounded flex flex-col p-4 bg-lightGray relative min-h-[290px]">
                        <div className="flex flex-row items-center">
                            <p className="pr-3 font-medium leading-[21px] font-instrument text-sm">Choose a headshot</p>
                            {' '}
                            <span className="bg-cirrusWhite px-2 rounded text-xs leading-[21px] h-[20px]">Optional</span>
                        </div>
                        <p className="font-normal text-sm pt-2 pb-6">This will be the image used on your official brief.</p>
                        <FileUpload />
                    </div>
                    <div className="mt-5">
                        <span className="font-medium text-cleanSlate text-sm">
                            <span>When is your birthday?</span>
                            {' '}
                            <span className="bg-lightGray text-xs px-2 rounded leading-[21px] h-[20px]">Optional</span>
                        </span>
                    </div>
                    <div className={styles.dateGroup}>
                        <div className={`${styles.dateSelect}`}>
                            <SingleSelect
                                options={Months}
                                name="month"
                                onChange={handleMonthSelect}
                                placeholder="Month"
                            />
                        </div>
                        <div className={`${styles.dateSelect}`}>
                            <SingleSelect
                                options={Days[selectedMonth.value ?? selectedMonth]}
                                name="day"
                                onChange={handleDaySelect}
                                placeholder="Day"
                            />
                        </div>
                        <div className={`${styles.dateSelect}`}>
                            <SingleSelect
                                options={Years}
                                name="year"
                                onChange={handleYearSelect}
                                placeholder="Year"
                            />
                        </div>
                    </div>
                    <p id="helper-text-explanation" className="text-sm text-cleanSlate dark:text-gray-400 mt-2">
                        All of this is optional.
                        Share the whole date, or just the month and day, if you like.
                    </p>
                </div>
            </div>
            <FormError error={status && status.errors?.fullName} />
        </Form>
    );
}

export default PersonalizeForm;
